import React, { useEffect, useState, useRef } from "react";
import AppWrapper from "../AppWrapper";
import { Page, Spinner, Select } from "@shopify/polaris";
import supabase from "../../supabase-client";
import { formatPlate } from "../../utility/funcs";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";
import { useBranch } from "../../context/BranchContext";
import "./OccupancyTimeline.css";

function OccupancyTimeline() {
  const navigate = useNavigate();
  const { branch } = useBranch();
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [carChanges, setCarChanges] = useState([]);
  // Live apartados (car_holds) in the window — drawn like reservations.
  const [holds, setHolds] = useState([]);
  const [possibleExt, setPossibleExt] = useState(new Set());
  const [category, setCategory] = useState("Todos");
  const [search, setSearch] = useState("");
  const [onlyFreeNow, setOnlyFreeNow] = useState(false);
  // Legend-driven filter. Click "Servicio" to keep only cars with a currently
  // active service reservation (type='service', status='active', covering now).
  // null = no filter. Add more keys here if other legend items become clickable.
  const [legendFilter, setLegendFilter] = useState(null);

  // Start from 7 days ago, show ~60 days
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").startOf("day")
  );
  const totalDays = 60;

  const fetchTimeline = async () => {
    setLoading(true);
    const from = startDate.format("YYYY-MM-DD");
    const to = startDate.clone().add(totalDays, "days").format("YYYY-MM-DD");

    const { data: rows, error } = await supabase.rpc("get_car_timeline", {
      date_from: from,
      date_to: to,
      p_branch_id: branch?.id || null,
    });
    if (error) {
      console.error(error.message);
      setLoading(false);
      return;
    }
    setData(rows);

    const { data: liveHolds } = await supabase
      .from("car_holds")
      .select("id, car_id, label, date_range, expires_at")
      .is("released_at", null)
      .is("converted_at", null)
      .gt("expires_at", new Date().toISOString())
      .overlaps("date_range", `[${from},${to}]`);
    setHolds(liveHolds || []);

    // Fetch car changes for reservations in the visible window
    const resIds = [...new Set((rows || []).map((r) => r.reservation_id).filter(Boolean))];

    // Reservations flagged "posible extensión" — drawn with a dashed outline.
    if (resIds.length) {
      const { data: flagged } = await supabase
        .from("reservations")
        .select("id")
        .eq("possible_extension", true)
        .eq("status", "active")
        .in("id", resIds);
      setPossibleExt(new Set((flagged || []).map((r) => r.id)));
    } else {
      setPossibleExt(new Set());
    }

    if (resIds.length) {
      const { data: changes } = await supabase
        .from("reservation_car_changes")
        .select("reservation_id, previous_car_id, new_car_id, changed_at")
        .in("reservation_id", resIds)
        .order("changed_at");
      setCarChanges(changes || []);
    } else {
      setCarChanges([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, branch]);

  // Scroll to today on load
  useEffect(() => {
    if (!loading && scrollRef.current) {
      const todayOffset = moment().diff(startDate, "days");
      const cellWidth = 32;
      scrollRef.current.scrollLeft = Math.max(0, todayOffset * cellWidth - 200);
    }
  }, [loading]);

  // Build dates array
  const dates = [];
  for (let i = 0; i < totalDays; i++) {
    dates.push(startDate.clone().add(i, "days"));
  }

  // Group months for header
  const months = [];
  let currentMonth = null;
  dates.forEach((d, i) => {
    const key = d.format("MMMM YYYY");
    if (key !== currentMonth) {
      months.push({ label: key, start: i, span: 1 });
      currentMonth = key;
    } else {
      months[months.length - 1].span++;
    }
  });

  // Group changes by reservation (already ordered by changed_at ASC from the query)
  const changesByRes = new Map();
  carChanges.forEach((ch) => {
    if (!changesByRes.has(ch.reservation_id)) changesByRes.set(ch.reservation_id, []);
    changesByRes.get(ch.reservation_id).push(ch);
  });

  // DB stores "pretend-local" times with +00 suffix. Strip TZ so moment parses as local.
  const stripTz = (ts) => (ts ? String(ts).substring(0, 19) : ts);

  // Group by car — use Map to preserve RPC insertion order (plain objects reorder numeric keys)
  // For sublease rows (car_id is null), key by `sub:<plate>` so multiple reservations on the same
  // subleased plate collapse into one row.
  const carMap = new Map();
  // First pass: create rows for every car/sublease that appears
  data.forEach((row) => {
    const key = row.is_sublease ? `sub:${row.plate}` : row.car_id;
    if (!carMap.has(key)) {
      carMap.set(key, {
        car_id: row.car_id,
        plate: row.plate,
        model_name: row.model_name,
        brand_name: row.brand_name,
        category_name: row.category_name,
        is_sublease: row.is_sublease,
        reservations: [],
      });
    }
  });

  // Second pass: attach each reservation to the correct row(s), splitting by car_changes
  const processedRes = new Set();
  data.forEach((row) => {
    if (!row.reservation_id || processedRes.has(row.reservation_id)) return;
    processedRes.add(row.reservation_id);

    const changes = changesByRes.get(row.reservation_id) || [];
    const resStart = stripTz(row.range_start);
    const resEnd = stripTz(row.range_end);

    // Clamp a timestamp to the reservation's actual range so a change logged
    // before the reservation starts (or after it ends) doesn't stretch bars.
    const clamp = (t) => {
      if (!t) return t;
      if (resStart && t < resStart) return resStart;
      if (resEnd && t > resEnd) return resEnd;
      return t;
    };

    // Build ordered segments: [{ carKey, start, end }, ...]
    // carKey for sublease segments uses the current row.plate (we don't track historical sublease plates).
    const currentKey = row.is_sublease ? `sub:${row.plate}` : row.car_id;
    const rawSegments = [];
    if (changes.length === 0) {
      rawSegments.push({ key: currentKey, start: resStart, end: resEnd });
    } else {
      const first = changes[0];
      rawSegments.push({
        key: first.previous_car_id == null ? `sub:${row.plate}` : first.previous_car_id,
        start: resStart,
        end: clamp(stripTz(first.changed_at)),
      });
      for (let i = 0; i < changes.length - 1; i++) {
        rawSegments.push({
          key: changes[i].new_car_id == null ? `sub:${row.plate}` : changes[i].new_car_id,
          start: clamp(stripTz(changes[i].changed_at)),
          end: clamp(stripTz(changes[i + 1].changed_at)),
        });
      }
      const last = changes[changes.length - 1];
      rawSegments.push({
        key: currentKey,
        start: clamp(stripTz(last.changed_at)),
        end: resEnd,
      });
    }

    // Drop empty/invalid segments (change before res start or after res end
    // collapses that side's segment to zero length).
    const segments = rawSegments.filter(
      (s) => s.start && (s.end == null || s.start < s.end)
    );

    const totalSegments = segments.length;
    segments.forEach((seg, idx) => {
      const car = carMap.get(seg.key);
      if (!car) return; // segment's car not in visible window — skip
      car.reservations.push({
        id: row.reservation_id,
        type: row.res_type,
        status: row.res_status,
        checked_out: row.checked_out,
        checked_in: row.checked_in,
        customer: row.customer_name,
        start: moment(seg.start),
        end: seg.end
          ? moment(seg.end)
          : startDate.clone().add(totalDays, "days"),
        isSegment: totalSegments > 1,
        segmentIndex: idx,
        totalSegments,
        possibleExtension: possibleExt.has(row.reservation_id),
      });
    });
  });

  holds.forEach((h) => {
    const car = carMap.get(h.car_id);
    const m = String(h.date_range).match(/^[[(]"?([^",]+)"?,\s*"?([^")\]]*)"?[)\]]$/);
    if (!car || !m) return;
    const end = m[2] && m[2] !== "infinity" ? stripTz(m[2]) : null;
    car.reservations.push({
      id: `hold-${h.id}`,
      holdId: h.id,
      type: "hold",
      status: "hold",
      customer: `Apartado${h.label ? ` · ${h.label}` : ""}${end ? ` · hasta ${moment(end).format("D MMM YYYY HH:mm")}` : ""}`,
      start: moment(stripTz(m[1])),
      end: end ? moment(end) : startDate.clone().add(totalDays, "days"),
      isSegment: false,
    });
  });

  let cars = Array.from(carMap.values());

  // Filter
  if (category !== "Todos") {
    cars = cars.filter((c) => c.category_name === category);
  }
  if (search.trim()) {
    const q = search.toLowerCase();
    cars = cars.filter(
      (c) =>
        c.plate?.toLowerCase().includes(q) ||
        c.model_name?.toLowerCase().includes(q) ||
        c.brand_name?.toLowerCase().includes(q)
    );
  }
  if (legendFilter) {
    // "Right now" legend filters: keep only cars that have a reservation in the
    // clicked state at this exact moment, and show just those matching bars.
    const nowLocal = moment(moment().format("YYYY-MM-DDTHH:mm:ss"));
    const isLast = (r) => !r.isSegment || r.segmentIndex === r.totalSegments - 1;
    const LEGEND_MATCHERS = {
      service: (r) =>
        r.type === "service" &&
        r.status === "active" &&
        r.start.isSameOrBefore(nowLocal) &&
        r.end.isAfter(nowLocal),
      inuse: (r) =>
        r.type === "rental" &&
        r.status === "active" &&
        r.checked_out &&
        !r.checked_in &&
        r.start.isSameOrBefore(nowLocal) &&
        r.end.isAfter(nowLocal),
      vencida: (r) =>
        r.type === "rental" &&
        r.status === "active" &&
        r.checked_out &&
        !r.checked_in &&
        r.end.isBefore(nowLocal) &&
        isLast(r),
    };
    const match = LEGEND_MATCHERS[legendFilter];
    if (match) {
      cars = cars
        .map((c) => ({ ...c, reservations: c.reservations.filter(match) }))
        .filter((c) => c.reservations.length > 0);
    }
  }
  if (onlyFreeNow) {
    // "Free" = no segment overlapping this exact moment, in any active state.
    // Pretend-local now (stripTz-style) so it aligns with the segment timestamps.
    const nowLocal = moment(moment().format("YYYY-MM-DDTHH:mm:ss"));
    cars = cars.filter((c) => {
      // Sublease rows are never "free" — they only exist when a sublease is booked
      if (c.is_sublease) return false;
      return !c.reservations.some(
        (r) =>
          (r.status === "active" || r.status === "hold") &&
          r.start.isSameOrBefore(nowLocal) &&
          r.end.isAfter(nowLocal)
      );
    });
  }

  const categories = ["Todos", ...new Set(data.map((r) => r.category_name).filter(Boolean))];

  const cellWidth = 32;

  const getBarProps = (res) => {
    const barStart = Math.max(0, res.start.diff(startDate, "days", true));
    const barEnd = Math.min(totalDays, res.end.diff(startDate, "days", true));
    const width = barEnd - barStart;
    if (width <= 0) return null;

    let className = "tl-bar tl-bar-reserved"; // default: rental active, not yet checked out
    if (res.type === "hold") className = "tl-bar tl-bar-hold";
    else if (res.type === "service") className = "tl-bar tl-bar-service";
    else if (res.status === "completed") className = "tl-bar tl-bar-completed";
    else if (res.status === "cancelled") className = "tl-bar tl-bar-cancelled";
    else if (res.checked_in && res.status === "active") className = "tl-bar tl-bar-pending"; // returned but not yet marked completed
    else if (res.checked_out && !res.checked_in) className = "tl-bar tl-bar-inuse"; // picked up

    // Check if overdue — only flag the final segment of a split reservation,
    // earlier segments ended because the car changed, not because the rental ran late.
    const isLastSegment = !res.isSegment || res.segmentIndex === res.totalSegments - 1;
    const isOverdue =
      isLastSegment &&
      res.type === "rental" &&
      res.status === "active" &&
      res.checked_out && !res.checked_in &&
      res.end.isBefore(moment());
    if (isOverdue) className = "tl-bar tl-bar-overdue";

    // Possible extension: dashed outline + ↻ on the segment that ends the rental.
    if (res.possibleExtension && isLastSegment) className += " tl-bar-possible-ext";

    // Mark continuation edges when a reservation is split across multiple cars
    if (res.isSegment) {
      className += " tl-bar-segment";
      if (res.segmentIndex > 0) className += " tl-bar-seg-left";
      if (res.segmentIndex < res.totalSegments - 1) className += " tl-bar-seg-right";
    }

    return {
      className,
      style: {
        left: barStart * cellWidth,
        width: width * cellWidth,
      },
    };
  };

  const prevMonth = () => setStartDate(startDate.clone().subtract(30, "days"));
  const nextMonth = () => setStartDate(startDate.clone().add(30, "days"));
  const goToday = () => setStartDate(moment().subtract(7, "days").startOf("day"));

  const dayLabels = ["D", "L", "M", "X", "J", "V", "S"];

  return (
    <AppWrapper>
      <Page title="Ocupación" backAction={{ url: "/reservations" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <Spinner size="small" />
          </div>
        ) : (
          <div className="tl-wrapper">
            {/* Controls */}
            <div className="tl-controls">
              <div className="tl-controls-left">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="tl-select"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="tl-search"
                />
                <label className="tl-free-toggle">
                  <input
                    type="checkbox"
                    checked={onlyFreeNow}
                    onChange={(e) => setOnlyFreeNow(e.target.checked)}
                  />
                  Solo libres ahora
                </label>
              </div>
              <div className="tl-controls-center">
                <button onClick={prevMonth} className="tl-nav-btn">‹</button>
                {months.map((m, i) => (
                  <span key={i} className="tl-month-label">{m.label}</span>
                ))}
                <button onClick={nextMonth} className="tl-nav-btn">›</button>
              </div>
              <button onClick={goToday} className="tl-today-btn">Hoy</button>
            </div>

            {/* Legend */}
            <div className="tl-legend">
              <span className="tl-legend-item"><span className="tl-dot tl-dot-reserved" /> Reservada</span>
              <span
                className={`tl-legend-item tl-legend-clickable ${legendFilter === "inuse" ? "tl-legend-active" : ""}`}
                onClick={() => setLegendFilter(legendFilter === "inuse" ? null : "inuse")}
                title="Filtrar a unidades en uso ahora"
              ><span className="tl-dot tl-dot-inuse" /> En uso</span>
              <span className="tl-legend-item"><span className="tl-dot tl-dot-pending" /> Pendiente</span>
              <span
                className={`tl-legend-item tl-legend-clickable ${legendFilter === "service" ? "tl-legend-active" : ""}`}
                onClick={() => setLegendFilter(legendFilter === "service" ? null : "service")}
                title="Filtrar a servicios activos"
              ><span className="tl-dot tl-dot-service" /> Servicio</span>
              <span className="tl-legend-item"><span className="tl-dot tl-dot-completed" /> Completada</span>
              <span className="tl-legend-item"><span className="tl-dot tl-dot-hold" /> Apartado</span>
              <span className="tl-legend-item"><span className="tl-dot tl-dot-possible-ext" /> Posible extensión</span>
              <span
                className={`tl-legend-item tl-legend-clickable ${legendFilter === "vencida" ? "tl-legend-active" : ""}`}
                onClick={() => setLegendFilter(legendFilter === "vencida" ? null : "vencida")}
                title="Filtrar a unidades vencidas (no devueltas)"
              ><span className="tl-dot tl-dot-overdue" /> Vencida</span>
            </div>

            {/* Timeline */}
            <div className="tl-container">
              {/* Car labels column (fixed) */}
              <div className="tl-labels">
                <div className="tl-label-header" />
                <div className="tl-label-header tl-day-header" />
                {cars.map((car, idx) => {
                  const prevWasFleet = idx > 0 && !cars[idx - 1].is_sublease;
                  const showSubleaseDivider = car.is_sublease && prevWasFleet;
                  const key = car.is_sublease ? `sub:${car.plate}` : `car:${car.car_id}`;
                  return (
                    <React.Fragment key={key}>
                      {showSubleaseDivider && (
                        <div className="tl-label-row tl-sublease-divider">
                          <span className="tl-sublease-divider-text">Subarrendados</span>
                        </div>
                      )}
                      <div
                        className={`tl-label-row ${car.is_sublease ? "tl-sublease-row" : ""}`}
                        onClick={() => !car.is_sublease && navigate(`/cars/${car.car_id}`)}
                        style={{ cursor: car.is_sublease ? "default" : "pointer" }}
                        title={car.is_sublease ? "Subarrendado" : "Ver detalle del auto"}
                      >
                        <span className="tl-car-name">
                          {formatPlate(car.plate)} {car.model_name}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Scrollable grid */}
              <div className="tl-scroll" ref={scrollRef}>
                {/* Month header */}
                <div className="tl-month-row" style={{ width: totalDays * cellWidth }}>
                  {months.map((m, i) => (
                    <div
                      key={i}
                      className="tl-month-cell"
                      style={{ width: m.span * cellWidth }}
                    >
                      {m.label}
                    </div>
                  ))}
                </div>

                {/* Day header */}
                <div className="tl-day-row" style={{ width: totalDays * cellWidth }}>
                  {dates.map((d, i) => {
                    const isToday = d.isSame(moment(), "day");
                    const isWeekend = d.day() === 0 || d.day() === 6;
                    return (
                      <div
                        key={i}
                        className={`tl-day-cell ${isToday ? "tl-today" : ""} ${isWeekend ? "tl-weekend" : ""}`}
                        style={{ width: cellWidth }}
                      >
                        <span className="tl-day-letter">{dayLabels[d.day()]}</span>
                        <span className="tl-day-number">{d.date()}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Car rows */}
                {cars.map((car, idx) => {
                  const prevWasFleet = idx > 0 && !cars[idx - 1].is_sublease;
                  const showSubleaseDivider = car.is_sublease && prevWasFleet;
                  const key = car.is_sublease ? `sub:${car.plate}` : `car:${car.car_id}`;
                  return (
                  <React.Fragment key={key}>
                  {showSubleaseDivider && (
                    <div className="tl-row tl-sublease-divider" style={{ width: totalDays * cellWidth }} />
                  )}
                  <div
                    className={`tl-row ${car.is_sublease ? "tl-sublease-row" : ""}`}
                    style={{ width: totalDays * cellWidth }}
                  >
                    {/* Weekend shading + today line */}
                    {dates.map((d, i) => {
                      const isToday = d.isSame(moment(), "day");
                      const isWeekend = d.day() === 0 || d.day() === 6;
                      return (
                        <div
                          key={i}
                          className={`tl-cell ${isWeekend ? "tl-weekend-bg" : ""} ${isToday ? "tl-today-bg" : ""}`}
                          style={{ left: i * cellWidth, width: cellWidth }}
                        />
                      );
                    })}

                    {/* Reservation bars */}
                    {car.reservations.map((res) => {
                      const props = getBarProps(res);
                      if (!props) return null;
                      return (
                        <div
                          key={res.id}
                          className={props.className}
                          style={props.style}
                          title={`${res.holdId ? "" : `#${res.id} `}${res.customer || "Servicio"}${res.possibleExtension ? " · Posible extensión" : ""}`}
                          onClick={() =>
                            navigate(
                              res.holdId
                                ? `/reservations/create?hold=${res.holdId}`
                                : `/reservation?id=${res.id}`
                            )
                          }
                        >
                          {props.style.width > 50 && (
                            <span className="tl-bar-label">
                              {res.status !== "completed" && res.type === "rental" && "$"}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Page>
    </AppWrapper>
  );
}

export default OccupancyTimeline;
